/**
 * Centralized API route definitions
 * This ensures consistency across the frontend and can be shared in a monorepo
 */

export const API_ROUTES = {
  // Auth endpoints
  AUTH: {
    SIGN_OUT: '/api/auth/sign-out',
    HEALTH: '/api/auth/health',
  },

  // Agent endpoints (Company Admin)
  AGENTS: {
    GET: '/api/agents',
    CREATE: '/api/agents',
    UPDATE: '/api/agents/:id',
  },

  // ElevenLabs proxy endpoints
  ELEVENLABS: {
    VOICES: '/api/elevenlabs/voices',
    TEXT_TO_SPEECH: '/api/elevenlabs/text-to-speech/:voiceId',
  },

  // Company endpoints (Company Admin)
  COMPANIES: {
    GET: '/api/companies',
    UPDATE: '/api/companies/:id',
  },

  // Knowledge Base endpoints (Company Admin)
  KNOWLEDGE_BASES: {
    GET_ALL: '/api/knowledge-bases',
    GET: '/api/knowledge-bases/:id',
    CREATE: '/api/knowledge-bases',
    UPDATE: '/api/knowledge-bases/:id',
    DELETE: '/api/knowledge-bases/:id',
  },

  // Admin endpoints (Super Admin)
  ADMIN: {
    // Agent endpoints
    AGENTS: {
      GET: '/api/admin/agents',
      CREATE: '/api/admin/agents',
      UPDATE: '/api/admin/agents/:id',
    },
    // Company endpoints
    COMPANIES: {
      SEARCH: '/api/admin/companies',
      GET_BY_ID: '/api/admin/companies/:id',
      UPDATE: '/api/admin/companies/:id',
    },
    // Knowledge Base endpoints (Super Admin)
    KNOWLEDGE_BASES: {
      GET_ALL: '/api/admin/knowledge-bases',
      GET: '/api/admin/knowledge-bases/:id',
      CREATE: '/api/admin/knowledge-bases',
      UPDATE: '/api/admin/knowledge-bases/:id',
      DELETE: '/api/admin/knowledge-bases/:id',
    },
  },

  // Health endpoints
  HEALTH: {
    API: '/api/health',
  },
} as const

export type ApiRoutes = typeof API_ROUTES

/**
 * Utility function to interpolate route parameters
 * @param route - The route template with :param placeholders
 * @param params - Object containing parameter values
 * @returns The route with parameters interpolated
 *
 * @example
 * interpolateRoute(API_ROUTES.AGENTS.GET_BY_ID, { id: '123' })
 * // Returns: '/api/agents/123'
 */
export function interpolateRoute(
  route: string,
  params: Record<string, string | number>,
): string {
  return Object.entries(params).reduce(
    (interpolatedRoute, [key, value]) =>
      interpolatedRoute.replace(new RegExp(`:${key}`, 'g'), String(value)),
    route,
  )
}
